import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  Input,
  Nav,
  NavItem,
  Row,
  UncontrolledTooltip,
  Modal,
  ModalBody,
  Label,
  Button,
  NavLink,
  ModalHeader,
  FormGroup,
  ModalFooter,
  Badge,
} from "reactstrap";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import "flatpickr/dist/flatpickr.min.css";

import { Link, useNavigate } from "react-router-dom";
import Select from "react-select";

import BreadCrumb from "../../Components/Common/BreadCrumb";
import DeleteModal from "../../Components/Common/DeleteModal";
import { jobApplication } from "../../common/data/appsJobs";
import StudentFormService from "../../services/KangarooServices/StudentFormService";
import StudentForm from "./component/StudentForm";
import toast from "react-hot-toast";
import CustomDataTable from "../../Components/CustomTable/CustomTable";

const StudentForms = () => {
  useEffect(() => {
    document.title = "Student Forms | Kangaroo Foundation";
  }, []);

  const navigate = useNavigate();
  const [show, setShow] = useState(false);
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
   const [filters, setFilters] = useState({
    searchText: "",
    status: "",
    fromDate: "",
    toDate: "",
    enumForm: "",
  });

  const [debouncedFilters, setDebouncedFilters] = useState(filters);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const [isStatusOpen, setIsStatusOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [adminNotes, setAdminNotes] = useState("");
  const [adminNotesError, setAdminNotesError] = useState("");
  const [selectedStatus, setSelectedStatus] = useState(null);

  const toggleStatus = () => setIsStatusOpen(!isStatusOpen);

 // ✅ debounce filters
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFilters(filters);
      setCurrentPage(1); // reset page
    }, 500);

    return () => clearTimeout(timer);
  }, [filters]);

  // Fetch data from server
  const fetchData = useCallback(async () => {
    setIsLoading(true);

    try {
      const response = await StudentFormService.getList({
        page: currentPage , // ✅ IMPORTANT (backend usually 0-based)
        pageSize,
        status: debouncedFilters.status || undefined,
        searchText: debouncedFilters.searchText || undefined,
        enumForm: debouncedFilters.enumForm || undefined,
        fromDate: debouncedFilters.fromDate || undefined,
        toDate: debouncedFilters.toDate || undefined,
      });

      setData(response?.items || []);
      setTotalCount(response?.totalCount || 0);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to fetch student forms");
      setData([]);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, pageSize, debouncedFilters]);

  // Re-fetch when filters, page, or page size change
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle status filter
  const handleStatusOption = (selectedOption) => {
    setFilters((prev) => ({
      ...prev,
      status: selectedOption ? selectedOption.value : "",
    }));
  };
  const handleFormOption = (selectedOption) => {
    setFilters((prev) => ({
      ...prev,
      enumForm: selectedOption ? selectedOption.value : "",
    }));
  };
  // Pagination handler
 const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  // Status options
  const statusOptions = [
    { value: 9, label: "New" },
    { value: 1, label: "Success" },
    { value: 2, label: "Contacted" },
    { value: 7, label: "Failed" },
    { value: 3, label: "Call Back Requested" },
    { value: 4, label: "Not Interested" },
    { value: 5, label: "Invalid Contact" },
    { value: 6, label: "Call Not Answered" },
    { value: 8, label: "In Touch Through Other Means" },
  ];
  const formOptions = [
    { value: 1, label: "Banner" },
    { value: 2, label: "Home page form" },
    { value: 3, label: "Service General Form" },
    { value: 4, label: "Service Detail Form" },
    { value: 5, label: "Newsletter" },
    { value: 6, label: "Consultation Form" },
    { value: 7, label: "Contact" },

    { value: 8, label: "Institution" },
  ];

  const statusColors = {
    Success: "success",
    CallBackRequested: "warning",
    Contacted: "info",
    New: "primary",
    Failed: "danger",
    InTouchThroughOtherMeans: "secondary",
    CallNotAnswered: "dark",
    NotInterested: "dark",
    InvalidContact: "danger",
  };

  // Columns
  const columns = useMemo(
    () => [
      {
        name: "SN",
        selector: (row, index) => index + 1 + (currentPage - 1) * pageSize,
        sortable: true,
        width: "75px",
      },
      {
        name: "Student Name",
        selector: (row) => row.fullName,
        cell: (row) => row.fullName || "N/A",
        sortable: true,
      },
      {
        name: "Email",
        selector: (row) => row.email,
        cell: (row) => row.email || "N/A",
        sortable: true,
      },
      {
        name: "Phone",
        selector: (row) => row.phone,
        cell: (row) => row.phone || "N/A",
        sortable: true,
      },
      {
        name: "Status",
        selector: (row) => row.status || "N/A",
        cell: (row) => {
          const color = statusColors[row.status] || "secondary";
          return <Badge color={color}>{row.status}</Badge>;
        },
        sortable: true,
      },
      {
        name: "Action",
        cell: (row) => (
          <div className="d-flex gap-2">
            <Link
              to={`/student-details/${row.id}`}
              className="btn btn-info btn-sm"
              title="View"
            >
              <i className="ri-eye-fill"></i>
            </Link>

            <Button
              size="sm"
              color="warning"
              onClick={() => handleStatusUpdate(row)}
            >
              <i className="ri-pencil-line"></i>
            </Button>
          </div>
        ),
        ignoreRowClick: true,
        allowOverflow: true,
        button: true,
      },
    ],
    [currentPage, pageSize]
  );

  // Status modal handlers
  const handleStatusUpdate = (item) => {
    setSelectedItem(item);
    const currentStatus = statusOptions.find(
      (status) => status.label === item.formStatus
    );
    setSelectedStatus(currentStatus ? currentStatus.value : null);
    toggleStatus();
  };

  const handleStatusChange = (e) => setSelectedStatus(Number(e.target.value));

  const handleUpdateStatus = async () => {
    if (!adminNotes) {
      setAdminNotesError("Admin Notes is required");
      return;
    }
    if (!selectedStatus) {
      toast.error("Please select a status");
      return;
    }

    if (selectedItem && selectedStatus !== null) {
      try {
        await StudentFormService.updateStatus(selectedItem.id, {
          adminNotes,
          status: selectedStatus,
        });
        toast.success("Status updated successfully");
        toggleStatus();
        setSelectedItem(null);
        setSelectedStatus(null);
        setAdminNotes("");
        setAdminNotesError("");
        await fetchData();
      } catch (error) {
        toast.error(
          error?.response?.data?.message || "Failed to update status"
        );
      }
    }
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Student Forms List" pageTitle="Student Forms" />
          <Row className="mb-3">
            <Col lg={4}>
              <div className="d-flex align-items-center gap-3">
                <Input
                  placeholder="Search"
                  value={filters.searchText}
                  onChange={(e) => setFilters((prev) => ({ ...prev, searchText: e.target.value }))}
                  className="w-100 shadow-sm"
                />
              </div>
            </Col>
            <Col>
              <Flatpickr
                className="form-control"
                options={{
                  mode: "range",
                  dateFormat: "Y-m-d",
                }}
                value={
                  filters.fromDate && filters.toDate
                    ? [filters.fromDate, filters.toDate]
                    : null
                }
                placeholder="Pick date range..."
                onChange={(dates) => {
                  if (dates.length === 0) {
                    setFilters((prev) => ({
                      ...prev,
                      fromDate: "",
                      toDate: "",
                    }));
                    return;
                  }

                  const [start, end] = dates;
                  setFilters((prev) => ({
                    ...prev,
                    fromDate: start ? start.toISOString().split("T")[0] : "",
                    toDate: end ? end.toISOString().split("T")[0] : "",
                  }));
                }}
              />
            </Col>
            <Col lg={2}>
              <Select
                options={statusOptions.map((s) => ({
                  label: s.label,
                  value: s.value,
                }))}
                value={statusOptions
                  .map((s) => ({ label: s.label, value: s.value }))
                  .find((option) => option.value === filters.status)}
                onChange={handleStatusOption}
                placeholder="Select Status"
                isClearable
                menuPortalTarget={document.body}
                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
              />
            </Col>
            <Col lg={3}>
              <Select
                options={formOptions?.map((s) => ({
                  label: s.label,
                  value: s.value,
                }))}
                value={formOptions
                  .map((s) => ({ label: s.label, value: s.value }))
                  .find((option) => option.value === filters.enumForm)}
                onChange={handleFormOption}
                placeholder="Select form"
                isClearable
                menuPortalTarget={document.body}
                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
              />
            </Col>
          </Row>
          {/* <Row>
            <Col>
              <div className="d-flex justify-content-end mb-2">
                <button
                  className="btn btn-success"
                  onClick={() => navigate("/student-forms/create")}
                >
                  <i className="ri-add-line align-bottom me-1"></i>
                  Form
                </button>
              </div>
            </Col>
          </Row> */}
          <Row>
            <Col lg={12}>
              <Card>
                <CardBody className="pt-0">
                  {isLoading ? (
                    <div className="text-center py-4">
                      <div
                        className="spinner-border text-primary"
                        role="status"
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : (
                 <CustomDataTable
  columns={columns}
  data={data}
  pagination
  paginationServer
  paginationTotalRows={totalCount}
  paginationDefaultPage={currentPage}   // ✅ add this
  onChangePage={handlePageChange}
  onChangeRowsPerPage={handlePageSizeChange}
  striped
/>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      <Modal isOpen={isStatusOpen} toggle={toggleStatus}>
        <ModalHeader toggle={toggleStatus}>Update Status</ModalHeader>
        <ModalBody>
          <div className="mb-3">
            <label htmlFor="statusSelect" className="form-label">
              Select Status
            </label>
            <select
              id="statusSelect"
              className="form-select"
              value={selectedStatus || selectedItem?.status || ""}
              onChange={handleStatusChange}
            >
              <option value="">-- Select Status --</option>
              {statusOptions.map((status) => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))}
            </select>
          </div>
          <FormGroup>
            <Label for="adminNotes">Admin Notes</Label>
            <Input
              id="adminNotes"
              name="adminNotes"
              type="textarea"
              rows={6}
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.target.value)}
              onBlur={() => {
                if (!adminNotes) setAdminNotesError("Admin Notes is required");
                else setAdminNotesError("");
              }}
              invalid={!!adminNotesError}
            />
            {adminNotesError && (
              <div className="text-danger mt-1">{adminNotesError}</div>
            )}
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <button className="btn btn-success" onClick={handleUpdateStatus}>
            Update
          </button>
          <button className="btn btn-danger" onClick={toggleStatus}>
            Cancel
          </button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default StudentForms;
